import './worklist.style.scss';

import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Input, message, Segmented } from 'antd';
import React, { useState } from 'react';

import { ActiveFilterBar } from '../../foundation/components/active_filter_bar';
import { WORKLIST_MESSAGES } from './constants';
import MySamplesTable from './MySamplesTable';
import PoolTable from './PoolTable';
import type { WorklistSample } from './types';
import { useWorklistTable } from './useWorklistTable';

const Worklist = () => {
  // Visual mock only: analyst/reviewer semantics are not specified yet.
  // Toggling changes nothing but the selected label.
  const [mockRole, setMockRole] = useState<'analyst' | 'reviewer'>('analyst');

  const {
    view,
    currentPage,
    searchInput,
    setSearchInput,
    appliedSearch,
    setAppliedSearch,
    handleSearch,
    setView,
    columnFilters,
    filterOptionsData,
    handleFilterChange,
    handleFilterClear,
    handleClearAllFilters,
    getActiveFiltersCount,
    handleTableChange,
    samples,
    totalResultCount,
    isFetching,
    poolCount,
    mineCount,
    handleClaim,
    isClaiming,
    pageSize,
  } = useWorklistTable();

  const onClaim = async (sample: WorklistSample) => {
    try {
      await handleClaim(sample);
    } catch {
      message.error(WORKLIST_MESSAGES.CLAIM_FAILED);
    }
  };

  return (
    <div className="worklist">
      <div className="worklist__toolbar">
        <div className="worklist__search-wrapper">
          <Input
            prefix={<FontAwesomeIcon icon={faSearch} />}
            className="worklist__search-input"
            placeholder="Sample ID, subject, indication..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onPressEnter={handleSearch}
          />
          <Button type="primary" loading={isFetching} onClick={handleSearch}>
            Search
          </Button>
        </div>
        <div className="worklist__role-toggle">
          <span className="worklist__role-label">Worklist view as</span>
          <Segmented
            value={mockRole}
            options={[
              { label: 'Analyst', value: 'analyst' },
              { label: 'Reviewer', value: 'reviewer' },
            ]}
            onChange={(value) => setMockRole(value as 'analyst' | 'reviewer')}
          />
        </div>
      </div>

      <div className="worklist__tabs-container">
        <div className="worklist__tabs">
          <div
            className={`worklist__tab ${view === 'mine' ? 'worklist__tab--active' : ''}`}
            onClick={() => {
              setView('mine');
              setSearchInput('');
            }}
          >
            My samples <span className="worklist__tab-count">{mineCount}</span>
          </div>
          <div
            className={`worklist__tab ${view === 'pool' ? 'worklist__tab--active' : ''}`}
            onClick={() => {
              setView('pool');
              setSearchInput('');
            }}
          >
            Pool <span className="worklist__tab-count">{poolCount}</span>
          </div>
        </div>

        <ActiveFilterBar
          columnFilters={columnFilters}
          searchTerm={appliedSearch}
          onFilterChange={handleFilterChange}
          onClearSearch={() => {
            setSearchInput('');
            setAppliedSearch('');
          }}
          onClearAll={handleClearAllFilters}
          getActiveFiltersCount={getActiveFiltersCount}
          classNamePrefix="worklist"
        />

        {view === 'pool' ? (
          <PoolTable
            columnFilters={columnFilters}
            currentPage={currentPage}
            filterOptionsData={filterOptionsData}
            handleClaim={onClaim}
            handleFilterChange={handleFilterChange}
            handleFilterClear={handleFilterClear}
            handleTableChange={handleTableChange}
            isClaiming={isClaiming}
            isFetching={isFetching}
            pageSize={pageSize}
            samples={samples}
            totalResultCount={totalResultCount}
          />
        ) : (
          <MySamplesTable
            columnFilters={columnFilters}
            currentPage={currentPage}
            filterOptionsData={filterOptionsData}
            handleFilterChange={handleFilterChange}
            handleFilterClear={handleFilterClear}
            handleTableChange={handleTableChange}
            isFetching={isFetching}
            pageSize={pageSize}
            samples={samples}
            totalResultCount={totalResultCount}
          />
        )}
      </div>
    </div>
  );
};

export default Worklist;
